import React, { useEffect } from "react";
import { FaGhost } from "react-icons/fa";
import ProductItem, { ProductItemType } from "./ProductSections/ProductItem";
import "./ProductRecycler.css";
import { useNavigate, useParams } from "react-router-dom";
import { useProducts } from "../hook/useProducts";
import Loader from "../components/Loader";
import ScreenTitle from "../components/ScreenTitle";
import api from "../api";

interface ProductRecyclerProps {
  type: ProductItemType;
  title?: string;
}

const ProductRecycler: React.FC<ProductRecyclerProps> = ({ type, title}) => {
  const navigate = useNavigate();
  const params   = useParams();

  const {
    products,
    setProducts,
    loading,
    loadingMore,
    hasMore,
    fetchNextPage,
  } = useProducts(type, params?.id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleQuantityChange = (productId: number, newQty: number) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, cartQuantity: newQty } : p));
  };

  const handleRemove = (productId: number) => {
    setProducts(prev => prev.filter(product => product.id !== productId));
  };

  const handleToggleFavorite = async (productId: number) => {
    const current = products.find(p => p.id === productId);
    const nextIsFavorite = !(current?.isFavorite ?? false);

    setProducts(prev =>
      prev.map(p => (p.id === productId ? { ...p, isFavorite: nextIsFavorite } : p))
    );

    try {
      await api.addRemoveFavourite(productId.toString());
      if (type === "favourites" && !nextIsFavorite) {
        setProducts(prev => prev.filter(p => p.id !== productId));
      }
    } catch (e) {
      console.error("Favourite toggle failed", e);
      setProducts(prev =>
        prev.map(p => (p.id === productId ? { ...p, isFavorite: !nextIsFavorite } : p))
      );
    }
  };

  const onBack = () => navigate(-1);

  const getHeaderTitle = () => {
    if (title) return title;
    const titles: Record<string, string> = {
      category:   "Collection",
      favourites: "Favorites",
      orders:     "Orders",
      search:     "Search Results",
    };
    return titles[type] || "Products";
  };

  if (loading)
    return (
      <div className="recycler-loading-state">
        <Loader size="large" type="spinner" text="Updating..." />
      </div>
    );

  return (
    <div className="recycler-canvas">
      <ScreenTitle onBack={onBack} getHeaderTitle={getHeaderTitle} />

      <main className="recycler-main-area">
        <div className="items-list-wrapper">
          {products.length === 0 ? (
            <div className="empty-state-card">
              <FaGhost className="ghost-icon" />
              <p>No Relevant Item Found</p>
              <button className="action-btn-black" onClick={onBack}>
                Return to Shop
              </button>
            </div>
          ) : (
            products.map(product => (
              <div className="product-item-row" key={product.id}>
                <ProductItem
                  onRemove={handleRemove}
                  onQuantityChange={handleQuantityChange}
                  onToggleFavorite={handleToggleFavorite}
                  product={product}
                  type={type}
                />
              </div>
            ))
          )}
        </div>

        {/* Load more — only for category / search */}
        {hasMore && (
          <div className="load-more-wrapper">
            <button
              className="load-more-btn"
              onClick={fetchNextPage}
              disabled={loadingMore}
            >
              {loadingMore
                ? <Loader size="small" type="spinner" />
                : "Load More"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProductRecycler;
